import React, { useEffect, useRef, useState } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import DynamicHighchart from '~/components/atoms/DynamicHighchart'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { dateFormat } from '~/utils/dateFormat'
import { getPriceIndexFromRomeo } from '~/utils/getPriceIndexFromRomeo'

import styles from './ArtistPrintIndex.module.css'

type DataType = {
  date: number
  value: number
}

export interface ArtistPrintIndexProps {
  testID?: string
  data?: DataType[]
  artist: string
  displayPest?: boolean
  artistId?: string
}

export default function ArtistPrintIndex({
  testID,
  data,
  artist,
  displayPest = false,
  artistId,
}: ArtistPrintIndexProps) {
  const [chartData, setChartData] = useState<any | null>(null)
  const t = useTranslations('common')

  const fetchDataFromRomeo = async () => {
    const splitArtistId = artistId?.split('-')[1]
    const response = await getPriceIndexFromRomeo({
      artistId: splitArtistId,
    })
    const { data: demandData } = response ?? {}

    return demandData?.series ?? []
  }

  useEffect(() => {
    if (!data) {
      ;(async () => {
        const dataFromRomeo = await fetchDataFromRomeo()
        setChartData(dataFromRomeo)
      })()
    } else {
      setChartData(data)
    }
  }, [data])

  useEffect(() => {
    return () => {
      setChartData(null)
    }
  }, [])

  const chartComponentRef = useRef<any>(null)

  const options = {
    title: {
      text: '',
      styledMode: true,
    },
    navigator: {
      enabled: true,
    },
    rangeSelector: {
      selected: 1,
    },
    xAxis: {
      title: { text: '' },
      uniqueNames: true,
      gridLineColor: '#EEE',
      startOnTick: false,
      endOnTick: false,
      minorGridLineWidth: 0,
      type: 'datetime',
      labels: {
        style: {
          fontFamily: 'Noir No1',
          fontWeight: 400,
          fontSize: 14,
        },
      },
      dateTimeLabelFormats: {
        month: {
          main: '%b %Y',
        },
      },
    },
    credits: {
      text: '© MyArtBroker',
    },
    yAxis: {
      title: {
        text: '',
      },
      gridLineColor: '#EEE',
      minorTicks: false,
      startOnTick: false,
      endOnTick: true,
      labels: {
        style: {
          fontFamily: 'Noir No1',
          fontWeight: 400,
          fontSize: 14,
        },
      },
    },
    chart: {
      type: 'spline',
      className: styles.highChartContainer,
      // scrollablePlotArea: {
      //   minWidth: 500,
      //   scrollPositionX: 2,
      //   opacity: 1,
      // },
    },
    tooltip: {
      backgroundColor: '#000',
      borderColor: '#000',
      borderWidth: 0,
      style: {
        color: '#fff',
        whitespace: '1',
        textAlign: 'center',
        fontFamily: 'Noir No1',
        fontSize: '14px',
      },
      shadow: false,
      xDateFormat: '%Y-%m-%d',
      formatter: function (this: any) {
        const { x, y } = this
        const dateValue = x as any
        const amountValue = y as number

        const formattedDate = dateFormat(
          '%d/%m/%Y',
          new Date(dateValue).getTime()
        )

        return `${formattedDate} <br /> ${amountValue.toFixed(0)}`
      },
    },
    plotOptions: {
      series: {
        allowPointSelect: true,
        marker: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
    },
    series: [
      {
        showInLegend: false,
        data: chartData && chartData,
        color: '#6068D6',
      },
    ],
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
  }

  if (!chartData) return null
  return (
    <div
      className={classNames(styles.base, {
        [styles.inArticleView]: !data,
      })}
      data-testid={testID}
    >
      <Container gutters={!data ? gutterTypes.upToMedium : undefined}>
        <Text
          Tag="h3"
          size={TextSizes.primaryTitleLrg}
          className={styles.title}
        >
          {t('printIndexTitle', { artist })}
        </Text>
        <Text Tag="p" size={TextSizes.bodyLrg} className={styles.body}>
          {t('printIndexDescription')}
          {displayPest && <>{t('pestControlVerification')}</>}.
        </Text>
        <DynamicHighchart options={options} ref={chartComponentRef} />
      </Container>
    </div>
  )
}
